/**
 * L'écran des journées — la file de travail du bouclage.
 *
 * Il prend la place du menu « Préparation », retiré le 12/09 : « on n'a jamais
 * utilisé le menu tournée ». Maquette `docs/maquettes/validation-journees.html`,
 * validée le 13/09.
 *
 *
 * DEUX NIVEAUX ICI, LE TROISIÈME PLUS TARD
 * ---------------------------------------------------------------------------
 * Le jour, puis les camions de ce jour. La journée d'un camion — ses arrêts, sa
 * route, ses kilomètres — est un écran à elle seule, et elle viendra avec son
 * lot. Tant qu'elle n'existe pas, ouvrir un camion n'a nulle part où mener :
 * AUCUNE LIGNE N'EST DONC CLIQUABLE, et l'écran le dit en toutes lettres
 * plutôt que d'offrir une porte qui ne s'ouvre pas.
 *
 *
 * UNE FILE, PAS UN CALENDRIER
 * ---------------------------------------------------------------------------
 * Les plus anciennes d'abord, les clôturées hors de vue. Sept ensembles font
 * une trentaine de lignes par semaine : la liste se vide, elle ne s'accumule
 * pas.
 *
 *
 * L'ÉTAT NE VIENT PAS DE L'API
 * ---------------------------------------------------------------------------
 * Elle rend des faits ; `journees.ts` en tire l'état avec des fonctions pures,
 * éprouvées par quinze tests. Cet écran ne calcule rien : il affiche.
 */

import { useCallback, useEffect, useState } from 'react';
import {
  enregistrerUnTrajet,
  lireJournees,
  lireListe,
  lireLaJourneeDuCamion,
  lireLesCamionsDuJour,
  modifierLaJournee,
  ordonnerLesArrets,
  type EchecApi,
  type LigneListe,
  type Resultat,
} from './api.js';
import { ANNUAIRE_SITES } from './sites.js';
import { euros } from './finance.js';
import {
  VueJourneeDuCamion,
  type EtatJourneeCamion,
} from './VueJourneeDuCamion.js';
import {
  ceQuiManque,
  ecartDit,
  ecartKilometrique,
  etatDuCamion,
  etatDuJour,
  jourDit,
  motDuCamion,
  motDuJour,
  restantAPointer,
  suiteDit,
  type CamionDuJour,
  type JourListe,
} from './journees.js';

interface Proprietes {
  readonly jeton: () => Promise<string | null>;
  readonly quandAccesPerdu: () => void;
}

type EtatEcran =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'panne' }
  | { readonly nom: 'prete'; readonly journees: readonly JourListe[] };

type EtatJourOuvert =
  | { readonly nom: 'chargement' }
  | { readonly nom: 'panne' }
  | { readonly nom: 'prete'; readonly camions: readonly CamionDuJour[] };

/**
 * Un nombre ou un tiret — jamais un zéro qui ferait croire à une mesure.
 *
 * ET EN FRANÇAIS. L'API rend « 1341 » et « 61.20 » : ce sont des valeurs de
 * transport, pas des valeurs d'affichage. Les poser telles quelles mettrait un
 * point décimal anglo-saxon à côté des montants français du reste de
 * l'application — le défaut du 11/09, retrouvé au rendu du 14/09.
 */
function kilometres(valeur: string | null): string {
  if (valeur === null) return '—';
  const n = Number(valeur);
  if (!Number.isFinite(n)) return '—';
  return new Intl.NumberFormat('fr-FR').format(n);
}

function peageDit(valeur: string | null): string {
  if (valeur === null) return '—';
  return euros(Number(valeur)) ?? '—';
}

/**
 * CE QUE L'API PEUT REFUSER, EN FRANÇAIS.
 *
 * EXHAUSTIVE PAR CONSTRUCTION — `Record<EchecApi, string>` : le compilateur
 * refuse un code sans phrase. C'est ce qui garantit qu'aucun refus ne
 * s'affichera un jour sous forme de code brut, comme cela arrivait avant le
 * 04/09.
 */
const REFUS: Readonly<Record<EchecApi, string>> = {
  acces: 'Votre accès a été refusé. Reconnectez-vous.',
  introuvable: 'Cette journée ou cet arrêt n’existe plus.',
  injoignable: 'Stafa ne répond pas. Vérifiez votre connexion et réessayez.',
  code_pris: 'Ce trajet a déjà été enregistré.',
  invalide: 'Cette saisie n’est pas acceptée.',
  conflit: 'Une autre saisie s’y oppose.',
  encore_actif: 'Désactivez d’abord.',
  encore_rattache: 'Cet élément est encore employé ailleurs.',
  ordre_annule: 'Cet ordre est annulé.',
  ordre_sans_etape: 'Cet ordre n’a aucune étape.',
  etape_validee: 'Cette étape est déjà validée.',
  jour_sans_etape: 'Ce jour ne porte aucune étape.',
  etape_en_tournee: 'Cette étape appartient à une tournée.',
  // LES DEUX SEULS QUI ARRIVENT VRAIMENT ICI.
  journee_cloturee:
    'Cette journée est clôturée. Rouvrez-la avant de la corriger — le bouton est en haut à droite.',
  journee_sans_chainage:
    'La journée précédente de ce camion ne dit pas où il s’est arrêté. Renseignez son lieu de fin d’abord : sans point de départ, les kilomètres d’aujourd’hui seraient faux en ayant l’air justes.',
  litige_deja_ouvert: 'Cette étape a déjà un litige ouvert.',
  litige_deja_resolu: 'Ce litige est déjà résolu.',
  litige_ouvert: 'Un litige est ouvert sur cette étape.',
  deja_facture: 'Cet ordre est déjà facturé.',
  rien_a_facturer: 'Rien à facturer.',
  facture_emise: 'Cette facture est émise.',
  facture_brouillon: 'Cette facture est encore un brouillon.',
  facture_vide: 'Cette facture est vide.',
  ordre_en_facturation: 'Cet ordre est en facturation.',
  affectation_excessive: 'Le montant dépasse ce qui reste dû.',
  piece_non_emise: 'Cette pièce n’est pas émise.',
  piece_autre_client: 'Cette pièce est celle d’un autre client.',
  gel_deja_actif: 'Un gel est déjà actif.',
  gel_absent: 'Aucun gel n’est actif.',
  numero_pas_le_dernier: 'Ce numéro n’est pas le dernier émis.',
  facture_reglee: 'Cette facture est réglée.',
  piece_relancee: 'Cette pièce a déjà été relancée.',
  facture_sans_echeance: 'Cette facture n’a pas d’échéance.',
  pennylane_deja_rattache: 'Déjà rattaché à Pennylane.',
  pennylane_client_non_rattache: 'Ce client n’est pas rattaché à Pennylane.',
  pennylane_impossible: 'Pennylane a refusé.',
  facture_pas_deposee:
    'Cette facture n\u2019est pas encore d\u00e9pos\u00e9e chez Pennylane : envoyez-la d\u2019abord.',
  facture_modifiee_depuis_apercu:
    'Cette facture a changé depuis le dernier aperçu. Recliquez « Aperçu Pennylane », vérifiez le document, puis validez.',
  avoir_sur_avoir: 'On ne fait pas un avoir sur un avoir.',
  facture_deja_avoiree: 'Cette facture a déjà un avoir.',
  avoir_trop_grand: 'Un avoir ne dépasse pas la facture qu’il corrige.',
  avoir_impossible: 'Cet avoir ne peut pas être établi.',
  service: 'Stafa n’a pas pu enregistrer. Réessayez dans un instant.',
};

export function EcranJournees({ jeton, quandAccesPerdu }: Proprietes) {
  const [etat, setEtat] = useState<EtatEcran>({ nom: 'chargement' });
  const [filtre, setFiltre] = useState<'ouvertes' | 'toutes'>('ouvertes');
  const [ouvert, setOuvert] = useState<string | null>(null);
  const [duJour, setDuJour] = useState<EtatJourOuvert>({ nom: 'chargement' });
  const [camion, setCamion] = useState<string | null>(null);
  const [laJournee, setLaJournee] = useState<EtatJourneeCamion>({ nom: 'chargement' });
  /**
   * LE COMPTEUR DE RELECTURE. Il ne sert à rien d'autre qu'à redéclencher la
   * lecture après une écriture : l'incrémenter change la dépendance de
   * l'effet, qui relit. Un `refetch()` fait maison ferait la même chose en
   * plus long, et dupliquerait la logique d'abandon.
   */
  const [relire, setRelire] = useState(0);
  const [refus, setRefus] = useState<string | null>(null);
  /**
   * L'ANNUAIRE DES SITES, pour choisir un lieu de début ou de fin.
   *
   * LU UNE FOIS À L'OUVERTURE DE L'ÉCRAN et non à chaque journée : il change
   * quelques fois par an, et le relire à chaque camion ferait une requête de
   * plus pour une liste identique. Un échec le laisse VIDE — les deux
   * sélecteurs n'offrent alors rien, ce qui se voit, plutôt que d'empêcher
   * l'écran de s'afficher.
   */
  const [sites, setSites] = useState<readonly LigneListe[]>([]);

  const avecJeton = useCallback(async (): Promise<string | null> => {
    const j = await jeton();
    if (j === null) quandAccesPerdu();
    return j;
  }, [jeton, quandAccesPerdu]);

  useEffect(() => {
    let abandonne = false;
    setEtat({ nom: 'chargement' });
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lues = await lireJournees(j, filtre);
      if (abandonne) return;
      if (lues.etat === 'ok') {
        setEtat({ nom: 'prete', journees: lues.valeur });
      } else {
        if (lues.cause === 'acces') quandAccesPerdu();
        setEtat({ nom: 'panne' });
      }
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton, filtre, quandAccesPerdu]);

  useEffect(() => {
    if (ouvert === null) return;
    let abandonne = false;
    setDuJour({ nom: 'chargement' });
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lus = await lireLesCamionsDuJour(j, ouvert);
      if (abandonne) return;
      if (lus.etat === 'ok') {
        setDuJour({ nom: 'prete', camions: lus.valeur });
      } else {
        if (lus.cause === 'acces') quandAccesPerdu();
        setDuJour({ nom: 'panne' });
      }
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton, ouvert, quandAccesPerdu]);

  useEffect(() => {
    let abandonne = false;
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lus = await lireListe(j, ANNUAIRE_SITES);
      if (!abandonne && lus.etat === 'ok') setSites(lus.valeur);
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton]);

  useEffect(() => {
    if (ouvert === null || camion === null) return;
    let abandonne = false;
    void (async () => {
      const j = await avecJeton();
      if (j === null || abandonne) return;
      const lue = await lireLaJourneeDuCamion(j, ouvert, camion);
      if (abandonne) return;
      if (lue.etat === 'ok') {
        setLaJournee({ nom: 'prete', donnees: lue.valeur });
      } else {
        if (lue.cause === 'acces') quandAccesPerdu();
        setLaJournee({ nom: 'panne' });
      }
    })();
    return () => {
      abandonne = true;
    };
  }, [avecJeton, ouvert, camion, relire, quandAccesPerdu]);

  /**
   * ÉCRIRE, PUIS RELIRE.
   *
   * LE REFUS EST DIT, PAS AVALÉ : sans ce bandeau, une clôture refusée ne
   * changerait rien à l'écran et l'utilisateur croirait avoir cliqué à côté.
   */
  const ecrire = useCallback(
    async (geste: (jeton: string) => Promise<Resultat<null>>) => {
      const j = await avecJeton();
      if (j === null) return;
      const resultat = await geste(j);
      if (resultat.etat === 'echec') {
        if (resultat.cause === 'acces') quandAccesPerdu();
        setRefus(REFUS[resultat.cause]);
        return;
      }
      setRefus(null);
      setRelire((n) => n + 1);
    },
    [avecJeton, quandAccesPerdu],
  );

  if (ouvert !== null && camion !== null) {
    return (
      <VueJourneeDuCamion
        jour={ouvert}
        etat={laJournee}
        sites={sites}
        refus={refus}
        gestes={{
          quandRetour: () => {
            setCamion(null);
          },
          quandTrajet: (depuis, vers, champs) => {
            void ecrire((j) =>
              enregistrerUnTrajet(j, ouvert, camion, {
                depuis_etape_id: depuis,
                vers_etape_id: vers,
                ...champs,
              }),
            );
          },
          quandDebut: (siteId) => {
            void ecrire((j) =>
              modifierLaJournee(j, ouvert, camion, { debut_site_id: siteId }),
            );
          },
          quandFin: (siteId) => {
            void ecrire((j) =>
              modifierLaJournee(j, ouvert, camion, { fin_site_id: siteId }),
            );
          },
          quandConsommation: (valeur) => {
            void ecrire((j) => modifierLaJournee(j, ouvert, camion, { consommation: valeur }));
          },
          quandOrdonner: (etapes) => {
            void ecrire((j) => ordonnerLesArrets(j, ouvert, camion, etapes));
          },
          quandPointer: (pointee) => {
            void ecrire((j) => modifierLaJournee(j, ouvert, camion, { pointee }));
          },
          quandCloturer: (cloturee) => {
            void ecrire((j) => modifierLaJournee(j, ouvert, camion, { cloturee }));
          },
        }}
      />
    );
  }

  if (ouvert !== null) {
    return (
      <VueCamionsDuJour
        jour={ouvert}
        etat={duJour}
        quandOuvrir={setCamion}
        quandRetour={() => {
          setOuvert(null);
        }}
      />
    );
  }

  return (
    <VueJournees
      etat={etat}
      filtre={filtre}
      quandFiltre={setFiltre}
      quandOuvrir={setOuvert}
    />
  );
}

/**
 * LE PREMIER NIVEAU, EN COMPOSANT À PROPRIÉTÉS — il ne décide rien et ne
 * demande rien à personne. C'est la découpe de `VueOrdres` et `VuePlanning`, et
 * elle sert à deux choses : ces vues se RENDENT hors API dans `apercu.tsx`, ce
 * qui permet de les regarder avant de les livrer ; et un écran qui ne sait pas
 * chercher ses données ne peut pas en inventer.
 */
export function VueJournees({
  etat,
  filtre,
  quandFiltre,
  quandOuvrir,
}: {
  readonly etat: EtatEcran;
  readonly filtre: 'ouvertes' | 'toutes';
  readonly quandFiltre: (v: 'ouvertes' | 'toutes') => void;
  readonly quandOuvrir: (jour: string) => void;
}) {
  return (
    <div className="corps corps-ot">
      <div className="barre-outils">
        <label className="champ-date">
          <span className="etiquette">Afficher</span>
          <select
            className="champ"
            value={filtre}
            onChange={(e) => {
              quandFiltre(e.target.value === 'toutes' ? 'toutes' : 'ouvertes');
            }}
          >
            <option value="ouvertes">Journées non clôturées</option>
            <option value="toutes">Toutes les journées</option>
          </select>
        </label>
        {/* TEXTE EXPLICATIF RETIRÉ — refonte graphique, maquettes validées le 31/10/2026 : « un logiciel ne se commente pas lui-même ». */}
      </div>

      {etat.nom === 'chargement' && <p className="attente">Lecture des journées…</p>}
      {etat.nom === 'panne' && (
        <p className="bandeau-refus">
          Les journées n’ont pas pu être lues. Réessayez dans un instant.
        </p>
      )}

      {etat.nom === 'prete' && etat.journees.length === 0 && (
        <p className="vide">
          Aucune journée à pointer. Un jour n’apparaît ici que s’il porte au moins
          une étape.
        </p>
      )}

      {etat.nom === 'prete' && etat.journees.length > 0 && (
        <div className="tableau-cadre">
          <table>
            <thead>
              <tr>
                <th scope="col">Jour</th>
                <th scope="col" className="col-souple">Ordres</th>
                <th scope="col">Camions</th>
                <th scope="col">Avancement</th>
                <th scope="col">Km relevés</th>
                <th scope="col">Écart</th>
                <th scope="col">Péage</th>
                <th scope="col">État</th>
              </tr>
            </thead>
            <tbody>
              {etat.journees.map((j) => {
                const mot = motDuJour(etatDuJour(j));
                const reste = restantAPointer(j);
                return (
                  <tr
                    key={j.jour}
                    className="rangee"
                    onClick={() => {
                      quandOuvrir(j.jour);
                    }}
                  >
                    <td>
                      <b>{jourDit(j.jour)}</b>
                    </td>
                    <td className="code">{j.ordres}</td>
                    <td className="code">{j.camions}</td>
                    <td>
                      {j.pointes} / {j.camions}
                      {reste > 0 && (
                        <span className="secondaire">
                          {reste === 1 ? '1 camion à pointer' : `${reste} camions à pointer`}
                        </span>
                      )}
                    </td>
                    <td className="code">{kilometres(j.km_reels)}</td>
                    <td className="code">
                      {ecartDit(ecartKilometrique(j.km_reels, j.km_estimes))}
                    </td>
                    <td className="code">{peageDit(j.peage)}</td>
                    <td>
                      <span className={`statut ${mot.classe}`}>{mot.texte}</span>
                      {j.etapes_sans_camion > 0 && (
                        <span className="secondaire">
                          {j.etapes_sans_camion === 1
                            ? '1 étape sans camion'
                            : `${j.etapes_sans_camion} étapes sans camion`}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

/** LE SECOND NIVEAU : les camions d'un jour. Même découpe, même raison. */
export function VueCamionsDuJour({
  jour,
  etat,
  quandOuvrir,
  quandRetour,
}: {
  readonly jour: string;
  readonly etat: EtatJourOuvert;
  /**
   * ELLE NE REÇOIT QUE DES CAMIONS, jamais `null` : la ligne « Aucun véhicule »
   * n'ouvre rien, parce que ces étapes n'appartiennent à aucune journée — il
   * n'y a pas de journée à ouvrir. On le dit dans la ligne plutôt que d'offrir
   * une porte qui donne sur le vide.
   */
  readonly quandOuvrir: (vehiculeId: string) => void;
  readonly quandRetour: () => void;
}) {
  return (
    <div className="corps corps-ot">
      <div className="barre-outils">
        <button type="button" className="lien-action" onClick={quandRetour}>
          ‹ Journées
        </button>
        <span className="etiquette">{jourDit(jour)}</span>
      </div>

      {etat.nom === 'chargement' && <p className="attente">Lecture des camions…</p>}
      {etat.nom === 'panne' && (
        <p className="bandeau-refus">Les camions de ce jour n’ont pas pu être lus.</p>
      )}

      {etat.nom === 'prete' && etat.camions.length === 0 && (
        <p className="vide">Aucun camion n’a roulé ce jour-là.</p>
      )}

      {etat.nom === 'prete' && etat.camions.length > 0 && (
        <>
          <div className="tableau-cadre">
            <table>
              <thead>
                <tr>
                  <th scope="col">Véhicule</th>
                  <th scope="col" className="col-souple">Chauffeur</th>
                  <th scope="col" className="col-souple">Remorque</th>
                  <th scope="col">Étapes</th>
                  <th scope="col" className="col-souple">Départ</th>
                  <th scope="col" className="col-souple">Fin de journée</th>
                  <th scope="col">Km</th>
                  <th scope="col">Péage</th>
                  <th scope="col">État</th>
                </tr>
              </thead>
              <tbody>
                {etat.camions.map((c) => {
                  const e = etatDuCamion(c);
                  const mot = motDuCamion(e);
                  const manque = e === 'incomplete' ? ceQuiManque(c) : null;
                  const ouvrable = c.vehicule_id !== null;
                  return (
                    <tr
                      key={c.vehicule_id ?? 'sans-camion'}
                      className="rangee"
                      onClick={
                        ouvrable
                          ? () => {
                              quandOuvrir(c.vehicule_id as string);
                            }
                          : undefined
                      }
                    >
                      <td>
                        {c.vehicule_id === null ? (
                          <b>Aucun véhicule</b>
                        ) : (
                          <b>{c.immatriculation ?? '—'}</b>
                        )}
                      </td>
                      <td>{suiteDit(c.chauffeurs)}</td>
                      <td>{suiteDit(c.remorques)}</td>
                      <td className="code">{c.etapes}</td>
                      <td>
                        {/* « inconnu » dit qu'on a cherché sans trouver. Pour des
                            étapes sans camion, il n'y a RIEN à chercher : elles
                            n'appartiennent à aucune journée, donc à aucun départ. */}
                        {c.vehicule_id === null ? (
                          '—'
                        ) : (
                          (c.depart_nom ?? <span className="absent">inconnu</span>)
                        )}
                        {c.depart_repris_du !== null && (
                          <span className="secondaire">
                            repris du {jourDit(c.depart_repris_du)}
                          </span>
                        )}
                      </td>
                      <td>{c.fin_nom ?? '—'}</td>
                      <td className="code">{kilometres(c.km_reels)}</td>
                      <td className="code">{peageDit(c.peage)}</td>
                      <td>
                        <span className={`statut ${mot.classe}`}>{mot.texte}</span>
                        {e === 'bloque' && (
                          <span className="secondaire">
                            la journée précédente de ce camion n’a pas de lieu de fin
                          </span>
                        )}
                        {e === 'sans_affectation' && (
                          <span className="secondaire">
                            ces étapes n’appartiennent à aucune journée
                          </span>
                        )}
                        {manque !== null && <span className="secondaire">il manque {manque}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {/* « CLIQUEZ SUR UN CAMION… » RETIRÉ — refonte graphique, maquettes
              validées le 31/10/2026. L'AVERTISSEMENT « Aucun véhicule » est
              gardé : c'est une alerte, pas une aide. */}
          {etat.camions.some((c) => c.vehicule_id === null) && (
            <p className="aide-saisie">
              La ligne « Aucun véhicule » ne s’ouvre pas : ces étapes
              n’appartiennent à aucune journée tant qu’un camion ne leur est
              pas affecté, depuis le planning.
            </p>
          )}
        </>
      )}
    </div>
  );
}
